import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register, startSession } from '../services/api';
import '../styles/AuthPage.css';

function RegisterPage() {
  const [form, setForm] = useState({ username: '', email: '', name: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const navigate = useNavigate();

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { username, email, name, password, confirm } = form;

    if (!username.trim() || !email.trim() || !password) {
      setError('Username, email, and password are required');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await register(username.trim(), email.trim().toLowerCase(), password, name.trim());
      if (data.verification_required) {
        setPendingEmail(data.user.email);
        return;
      }
      startSession(data.user, data.token);
      navigate('/papers');
    } catch (err) {
      const msg = err.response?.data?.error;
      setError(msg || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (pendingEmail) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <h1>PaperBuddy</h1>
          <p className="auth-subtitle">Check your email</p>
          <p className="auth-subtitle">
            We sent a verification link to <strong>{pendingEmail}</strong>. Open it to
            finish setting up your account.
          </p>
          <div className="auth-links">
            <Link to="/" className="auth-link">Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1>PaperBuddy</h1>
        <p className="auth-subtitle">Create your account</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username <span className="required">*</span></label>
            <input
              id="username"
              type="text"
              value={form.username}
              onChange={set('username')}
              placeholder="e.g. jsmith"
              disabled={loading}
              autoFocus
              autoComplete="username"
            />
            <span className="field-hint">Letters, numbers, underscores, hyphens, periods</span>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email <span className="required">*</span></label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder="you@example.com"
              disabled={loading}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">Display name <span className="optional">(optional)</span></label>
            <input
              id="name"
              type="text"
              value={form.name}
              onChange={set('name')}
              placeholder="Jane Smith"
              disabled={loading}
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password <span className="required">*</span></label>
            <input
              id="password"
              type="password"
              value={form.password}
              onChange={set('password')}
              placeholder="At least 8 characters"
              disabled={loading}
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm">Confirm password <span className="required">*</span></label>
            <input
              id="confirm"
              type="password"
              value={form.confirm}
              onChange={set('confirm')}
              placeholder="Repeat your password"
              disabled={loading}
              autoComplete="new-password"
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <div className="auth-links">
          Already have an account?{' '}
          <Link to="/" className="auth-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
